import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './icons';

// Calendario e orari su misura (al posto di quelli di sistema), uguali su Windows, Mac e web.

const pad = (n: number) => String(n).padStart(2, '0');
export const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseDay = (v: string) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(v + 'T12:00:00') : null);
const MONTHS = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const WD = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];

export const dayLabel = (v: string) => {
  const d = parseDay(v);
  return d ? d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '';
};

/** Finestrella agganciata a un campo: posizione fissa (non viene tagliata dalle finestre che scorrono). */
function usePopover(open: boolean, setOpen: (v: boolean) => void, height: number) {
  const anchor = useRef<HTMLDivElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    const place = () => {
      const r = anchor.current!.getBoundingClientRect();
      const below = window.innerHeight - r.bottom > height + 12;
      const left = Math.min(Math.max(8, r.left), window.innerWidth - 300);
      setPos({ left, top: below ? r.bottom + 6 : Math.max(8, r.top - height - 6), width: r.width });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, height]);

  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!anchor.current?.contains(t) && !pop.current?.contains(t)) setOpen(false);
    };
    // Esc chiude solo il calendario, non la finestra sotto
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', down, true);
    window.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('mousedown', down, true);
      window.removeEventListener('keydown', key, true);
    };
  }, [open, setOpen]);

  return { anchor, pop, pos };
}

// ---------------------------------------------------------------------------

export interface DateInputProps {
  value: string;
  onChange: (v: string) => void;
  /** Si può lasciare vuota (mostra «Nessuna data») */
  allowEmpty?: boolean;
  placeholder?: string;
  min?: string;
  /** Quanti impegni ha quel giorno (puntini sotto il numero) */
  busy?: (day: string) => number;
  /** Il calendario è passato a un altro mese (per caricare gli impegni) */
  onMonth?: (firstDay: string) => void;
}

export function DateInput({ value, onChange, allowEmpty, placeholder, min, busy, onMonth }: DateInputProps) {
  const [open, setOpen] = useState(false);
  const { anchor, pop, pos } = usePopover(open, setOpen, 340);
  const sel = parseDay(value);
  const [view, setView] = useState(() => {
    const d = sel ?? new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  useEffect(() => {
    if (open && sel) setView(new Date(sel.getFullYear(), sel.getMonth(), 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (open) onMonth?.(dayKey(view));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view]);

  const days = useMemo(() => {
    const first = new Date(view);
    const shift = (first.getDay() + 6) % 7;
    const start = new Date(first.getFullYear(), first.getMonth(), 1 - shift);
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [view]);

  const today = dayKey(new Date());
  const move = (m: number) => setView((v) => new Date(v.getFullYear(), v.getMonth() + m, 1));
  const pick = (k: string) => {
    onChange(k);
    setOpen(false);
  };

  return (
    <div className="dp" ref={anchor}>
      <button type="button" className={`dp-field${open ? ' open' : ''}`} onClick={() => setOpen((o) => !o)}>
        <Icon name="calendar" size={16} />
        <span className={value ? '' : 'muted'}>{value ? dayLabel(value) : (placeholder ?? 'Scegli una data')}</span>
      </button>
      {open && pos && (
        <div className="dp-pop" ref={pop} style={{ left: pos.left, top: pos.top }}>
          <div className="dp-head">
            <button type="button" className="btn icon ghost small" onClick={() => move(-1)} title="Mese prima">
              <Icon name="chevronLeft" size={16} />
            </button>
            <b>
              {MONTHS[view.getMonth()]} {view.getFullYear()}
            </b>
            <button type="button" className="btn icon ghost small" onClick={() => move(1)} title="Mese dopo">
              <Icon name="chevronRight" size={16} />
            </button>
          </div>
          <div className="dp-grid">
            {WD.map((w) => (
              <span key={w} className="dp-wd">
                {w}
              </span>
            ))}
            {days.map((d) => {
              const k = dayKey(d);
              const other = d.getMonth() !== view.getMonth();
              const off = !!min && k < min;
              const n = busy?.(k) ?? 0;
              return (
                <button
                  type="button"
                  key={k}
                  disabled={off}
                  className={`dp-day${other ? ' other' : ''}${k === today ? ' today' : ''}${k === value ? ' on' : ''}${d.getDay() % 6 === 0 ? ' we' : ''}`}
                  onClick={() => pick(k)}
                  title={n ? `${n} ${n === 1 ? 'impegno' : 'impegni'}` : undefined}
                >
                  {d.getDate()}
                  {n > 0 && (
                    <span className="dp-dots">
                      {Array.from({ length: Math.min(n, 3) }, (_, i) => (
                        <i key={i} />
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="dp-foot">
            <button type="button" className="btn small ghost" onClick={() => pick(today)}>
              Oggi
            </button>
            <button type="button" className="btn small ghost" onClick={() => pick(dayKey(new Date(Date.now() + 86_400_000)))}>
              Domani
            </button>
            {allowEmpty && value && (
              <button type="button" className="btn small ghost" onClick={() => pick('')}>
                Nessuna data
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Orario: si scrive (es. 930 → 09:30) oppure si sceglie dalla lista a quarti d'ora. */
export function TimeInput({ value, onChange, from = 7, to = 21, step = 15 }: { value: string; onChange: (v: string) => void; from?: number; to?: number; step?: number }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value);
  const { anchor, pop, pos } = usePopover(open, setOpen, 260);
  useEffect(() => setText(value), [value]);

  const times = useMemo(() => {
    const out: string[] = [];
    for (let m = from * 60; m <= to * 60; m += step) out.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`);
    return out;
  }, [from, to, step]);

  useEffect(() => {
    if (!open || !pop.current) return;
    const el = pop.current.querySelector<HTMLElement>('.on') ?? pop.current.querySelector<HTMLElement>(`[data-t="${value.slice(0, 3)}00"]`);
    el?.scrollIntoView({ block: 'center' });
  }, [open, pos, value, pop]);

  const commit = () => {
    const digits = text.replace(/\D/g, '');
    let h: number, m: number;
    if (/^\d{1,2}[:.]\d{2}$/.test(text.trim())) [h, m] = text.trim().split(/[:.]/).map(Number) as [number, number];
    else if (digits.length === 3 || digits.length === 4) [h, m] = [Number(digits.slice(0, -2)), Number(digits.slice(-2))];
    else if (digits.length >= 1 && digits.length <= 2) [h, m] = [Number(digits), 0];
    else return setText(value);
    if (h > 23 || m > 59) return setText(value);
    const v = `${pad(h)}:${pad(m)}`;
    setText(v);
    if (v !== value) onChange(v);
  };

  return (
    <div className="dp" ref={anchor}>
      <div className="tp-field">
        <Icon name="clock" size={16} />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={(e) => {
            e.target.select();
            setOpen(true);
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
              setOpen(false);
            }
          }}
          inputMode="numeric"
          placeholder="--:--"
          maxLength={5}
        />
      </div>
      {open && pos && (
        <div className="dp-pop tp-pop" ref={pop} style={{ left: pos.left, top: pos.top, width: Math.max(pos.width, 150) }}>
          {times.map((t) => (
            <button
              type="button"
              key={t}
              data-t={t}
              className={`tp-item${t === value ? ' on' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(t);
                setText(t);
                setOpen(false);
              }}
            >
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Data e ora insieme, valore nel formato di <input type="datetime-local"> (AAAA-MM-GGTHH:MM). */
export function DateTimeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [d, t] = value ? value.split('T') : ['', ''];
  return (
    <div className="dt-pair">
      <DateInput value={d ?? ''} onChange={(nd) => onChange(nd ? `${nd}T${t || '09:00'}` : '')} />
      <TimeInput value={(t ?? '').slice(0, 5)} onChange={(nt) => onChange(`${d || dayKey(new Date())}T${nt}`)} />
    </div>
  );
}
